import * as React from 'react';
import { Tooltip } from '@mui/material';

// A tooltip that only appears when its (single, truncating) child actually overflows
// — i.e. its text is clipped with an ellipsis. Names that fit fully show no popup at
// all, so lists stay quiet during scanning; only clipped names reveal their full text
// on a deliberate hover. `enterDelay` keeps a quick scan/click from triggering it.
//
// The child must be a single element that forwards a ref to a DOM node carrying the
// overflow styles (e.g. a Typography or span with whiteSpace:'nowrap'; overflow:'hidden';
// textOverflow:'ellipsis').
export function OverflowTooltip({ title, placement = 'top-start', enterDelay = 600, children, ...rest }) {
    const nodeRef = React.useRef(null);
    const observerRef = React.useRef(null);
    const [overflowing, setOverflowing] = React.useState(false);

    const measure = React.useCallback(() => {
        const el = nodeRef.current;
        // +1 guards against sub-pixel rounding falsely reporting overflow.
        setOverflowing(!!el && el.scrollWidth > el.clientWidth + 1);
    }, []);

    const handleRef = React.useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        nodeRef.current = node;
        if (node) {
            measure();
            if (typeof ResizeObserver !== 'undefined') {
                observerRef.current = new ResizeObserver(() => measure());
                observerRef.current.observe(node);
            }
        }
    }, [measure]);

    return (
        <Tooltip
            title={overflowing ? title : ''}
            arrow
            placement={placement}
            enterDelay={enterDelay}
            enterNextDelay={enterDelay}
            {...rest}
        >
            {React.cloneElement(children, {
                ref: handleRef,
                onMouseEnter: (e) => { measure(); children.props.onMouseEnter?.(e); },
            })}
        </Tooltip>
    );
}
